class WishlistPage extends HTMLElement {
  static #BATCH_SIZE = 20;
  static #SWYM_TIMEOUT = 10000;

  #swat = null;
  #config = {};
  #items = [];
  #itemsByHandle = new Map();
  #cards = new Map();
  #visible = [];
  #filters = new Map();
  #sort = 'added-desc';
  #undoTimer = null;
  #lastRemoved = null;
  #loading = false;
  #reload = false;

  #stickyWrapper = null;
  #stickyOffset = null;

  #onKeydown = event => {
    if (event.key === 'Escape') this.#closePickers();
  };

  #onScroll = () => {
    const offset = this.#stickyTop();
    const { top } = this.#stickyWrapper.getBoundingClientRect();

    this.classList.toggle('filters-sticky', offset !== null && Math.abs(top - offset) < 1);
  };

  #onCustomerRefreshed = () => this.#load();

  #onResize = () => {
    this.#stickyOffset = null;
    this.#onScroll();
  };

  constructor() {
    super();

    this.#config = WishlistPage.#parseConfig(this.querySelector('[data-wishlist-config]'));
    this.undoDuration = (Number(this.dataset.undoDuration) || 10) * 1000;
    this.cardsSection = this.dataset.cardsSection;

    this.grid = this.querySelector('[data-wishlist-grid]');
    this.loader = this.querySelector('[data-wishlist-loader]');
    this.toolbar = this.querySelector('[data-wishlist-toolbar]');
    this.countEl = this.querySelector('[data-wishlist-count]');
    this.emptyEl = this.querySelector('[data-wishlist-empty]');
    this.errorEl = this.querySelector('[data-wishlist-error]');
    this.noResultsEl = this.querySelector('[data-wishlist-no-results]');
    this.filtersEl = this.querySelector('[data-wishlist-filters]');
    this.filtersInner = this.querySelector('[data-wishlist-filters-inner]');
    this.pillsEl = this.querySelector('[data-wishlist-pills]');
    this.pillsList = this.querySelector('[data-wishlist-pills-list]');
    this.toast = this.querySelector('[data-wishlist-toast]');
    this.printArea = this.querySelector('[data-wishlist-print-area]');

    this.filterTemplate = this.querySelector('[data-wishlist-filter-template]');
    this.valueTemplate = this.querySelector('[data-wishlist-value-template]');
    this.pillTemplate = this.querySelector('[data-wishlist-pill-template]');

    this.#bindEvents();
  }

  async connectedCallback() {
    document.addEventListener('keydown', this.#onKeydown);
    this.#watchSticky();

    try {
      this.#swat = await WishlistPage.#swymReady();
      this.#swat.evtLayer?.addEventListener('sw:customerInfoRefreshed', this.#onCustomerRefreshed);
      await this.#load();
    } catch (error) {
      console.error('Wishlist: could not load the wishlist', error);
      this.#showEmpty();
    }
  }

  disconnectedCallback() {
    this.#swat?.evtLayer?.removeEventListener('sw:customerInfoRefreshed', this.#onCustomerRefreshed);
    document.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    clearTimeout(this.#undoTimer);
  }

  #watchSticky() {
    this.#stickyWrapper = this.querySelector('[data-sticky-filters]');
    if (!this.#stickyWrapper) return;

    this.#onScroll();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize);
  }

  #stickyTop() {
    if (this.#stickyOffset !== null) return this.#stickyOffset;

    const offset = parseFloat(getComputedStyle(this.#stickyWrapper).top);
    if (Number.isNaN(offset)) return null;

    this.#stickyOffset = offset;

    return offset;
  }

  static #parseConfig(element) {
    try {
      return JSON.parse(element?.textContent || '{}');
    } catch (error) {
      console.error('Wishlist: invalid config', error);

      return {};
    }
  }

  // Without the timeout a missing or broken Swym leaves the page on skeletons.
  static #swymReady() {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Swym did not initialise')), WishlistPage.#SWYM_TIMEOUT);

      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(swat => {
        clearTimeout(timer);
        resolve(swat || window._swat);
      });
    });
  }

  static #clone(template) {
    return template.content.firstElementChild.cloneNode(true);
  }

  static #datasetKey(key) {
    return `filter${key.replace(/(^|-)([a-z])/g, (match, dash, letter) => letter.toUpperCase())}`;
  }

  static #handle(url) {
    const match = String(url ?? '').split('?')[0].match(/\/products\/([^/]+)/);

    return match ? match[1].toLowerCase() : '';
  }

  #bindEvents() {
    this.addEventListener('click', event => {
      const target = (selector) => event.target.closest(selector);

      if (target('[data-wishlist-remove]')) {
        event.preventDefault();
        this.#remove(target('[data-wishlist-card]'));
      } else if (target('[data-wishlist-picker-open]')) {
        event.preventDefault();
        this.#togglePicker(target('[data-wishlist-card]'));
      } else if (target('[data-wishlist-variant]')) {
        event.preventDefault();
        this.#addToCart(target('[data-wishlist-variant]'));
      } else if (target('[data-wishlist-undo]')) {
        event.preventDefault();
        this.#undo();
      } else if (target('[data-wishlist-print]')) {
        event.preventDefault();
        this.#print();
      } else if (target('[data-wishlist-pill]')) {
        event.preventDefault();
        this.#uncheck(target('[data-wishlist-pill]').dataset);
      } else if (target('[data-wishlist-filters-clear]')) {
        event.preventDefault();
        this.#clearFilters();
      } else if (target('[data-wishlist-filters-apply]')) {
        event.preventDefault();
        this.#closeFilters();
      } else if (!target('[data-wishlist-picker]')) {
        this.#closePickers();
      }
    });

    this.querySelector('[data-wishlist-sort]')?.addEventListener('change', event => {
      this.#sort = event.target.value;
      this.#render();
    });

    this.filtersInner?.addEventListener('change', event => {
      const input = event.target.closest('input[type="checkbox"]');
      if (!input) return;

      const { filterKey } = input.dataset;

      if (input.checked) {
        const values = this.#filters.get(filterKey) ?? new Set();

        values.add(input.value);
        this.#filters.set(filterKey, values);
      } else {
        this.#dropFilterValue(filterKey, input.value);
      }

      this.#render();
    });
  }

  async #load() {
    // Swym can refresh the list mid load - remember it and run once more.
    if (this.#loading) {
      this.#reload = true;
      return;
    }

    this.#loading = true;
    this.#reload = false;
    this.#cards.clear();
    this.grid.innerHTML = '';
    this.#setState('loading');

    try {
      this.#setItems(this.#dedupe(await this.#fetchWishlist()));

      if (!this.#items.length) {
        this.#showEmpty();
        return;
      }

      await this.#loadCards();

      // Products deleted or unpublished since they were saved never render.
      this.#setItems(this.#items.filter(item => this.#cards.has(item.handle)));

      if (!this.#cards.size) {
        this.#showError();
        return;
      }

      this.#setState('ready');
      this.#buildFilters();
      this.#render();
    } finally {
      this.#loading = false;

      if (this.#reload) await this.#load();
    }
  }

  #setItems(items) {
    this.#items = items;
    this.#itemsByHandle = new Map(items.map(item => [item.handle, item]));
    this.#updateCount();
  }

  #fetchWishlist() {
    return new Promise((resolve, reject) => {
      const done = products => resolve(Array.isArray(products) ? products : []);
      const fetchList = this.#swat.fetchWishlist ?? this.#swat.fetch;

      if (!fetchList) {
        reject(new Error('Swym fetch API is not available'));
        return;
      }

      fetchList.call(this.#swat, done, reject);
    });
  }

  #dedupe(entries) {
    const products = new Map();

    entries.forEach(entry => {
      const handle = WishlistPage.#handle(entry.du);
      if (!handle) return;

      const product = products.get(handle);

      if (product) {
        product.entries.push(entry);
        product.addedAt = Math.max(product.addedAt, Number(entry.cts) || 0);
      } else {
        products.set(handle, { handle, addedAt: Number(entry.cts) || 0, entries: [entry] });
      }
    });

    return [...products.values()];
  }

  async #loadCards() {
    const handles = this.#items.map(item => item.handle);

    for (let i = 0; i < handles.length; i += WishlistPage.#BATCH_SIZE) {
      const batch = handles.slice(i, i + WishlistPage.#BATCH_SIZE);

      await this.#fetchCards(`search?q=${encodeURIComponent(batch.join(' '))}&type=article`);

      const missing = batch.filter(handle => !this.#cards.has(handle));

      await Promise.all(missing.map(handle => this.#fetchCards(`products/${handle}`)));
    }
  }

  async #fetchCards(path) {
    const separator = path.includes('?') ? '&' : '?';
    const url = `${window.Shopify.routes.root}${path}${separator}section_id=${this.cardsSection}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Status ${response.status}`);

      this.#collectCards(await response.text());
    } catch (error) {
      console.error(`Wishlist: could not render cards for ${path}`, error);
    }
  }

  #collectCards(html) {
    const holder = document.createElement('div');
    holder.innerHTML = html;

    holder.querySelectorAll('[data-wishlist-card]').forEach(card => {
      const item = this.#itemsByHandle.get(card.dataset.handle);
      if (!item) return;

      card.dataset.addedAt = item.addedAt;
      this.#applyCardStrings(card);
      this.#cards.set(card.dataset.handle, card);
    });
  }

  #applyCardStrings(card) {
    const { choose_size: chooseSize, remove_label: removeLabel } = this.#config.strings ?? {};

    card.querySelector('[data-wishlist-picker-title]')?.replaceChildren(chooseSize ?? '');
    card.querySelector('[data-wishlist-picker-open]')?.setAttribute('aria-label', chooseSize ?? '');
    card.querySelector('[data-wishlist-remove]')?.setAttribute('aria-label', removeLabel ?? '');
  }

  #cardValues(card, key) {
    const raw = key === 'availability'
      ? (card.dataset.available === 'true' ? 'in-stock' : 'out-of-stock')
      : card.dataset[WishlistPage.#datasetKey(key)];

    return new Set((raw || '').split('|').map(value => value.trim()).filter(Boolean));
  }

  #valueLabel(key, value) {
    if (key !== 'availability') return value;

    const { in_stock: inStock, out_of_stock: outOfStock } = this.#config.strings ?? {};

    return value === 'in-stock' ? inStock ?? value : outOfStock ?? value;
  }

  #buildFilters() {
    const cards = [...this.#cards.values()];

    const groups = (this.#config.filters ?? []).map(filter => {
      const values = new Set(cards.flatMap(card => [...this.#cardValues(card, filter.key)]));

      return { ...filter, values: [...values].sort((a, b) => a.localeCompare(b)) };
    }).filter(group => group.values.length > 1);

    this.filtersInner.replaceChildren(...groups.map((group, index) => this.#filterGroup(group, index)));
    this.filtersEl.hidden = !groups.length;
  }

  #filterGroup(group, index) {
    const node = WishlistPage.#clone(this.filterTemplate);

    node.dataset.index = index + 1;
    node.dataset.filterGroup = group.key;
    node.querySelectorAll('[data-label]').forEach(label => {
      label.textContent = group.label;
    });

    const values = group.values.map((value, valueIndex) => this.#filterValue(group.key, value, valueIndex));
    node.querySelector('[data-values]').replaceChildren(...values);

    return node;
  }

  #filterValue(key, value, index) {
    const node = WishlistPage.#clone(this.valueTemplate);
    const input = node.querySelector('input');
    const id = `WishlistFilter-${key}-${index}`;

    input.id = id;
    input.value = value;
    input.dataset.filterKey = key;

    node.querySelector('label').htmlFor = id;
    node.querySelector('[data-label]').textContent = this.#valueLabel(key, value);

    return node;
  }

  #matches(card) {
    for (const [key, selected] of this.#filters) {
      const values = this.#cardValues(card, key);

      if (![...selected].some(value => values.has(value))) return false;
    }

    return true;
  }

  #sortCards(cards) {
    const byText = (a, b) => a.dataset.sortTitle.localeCompare(b.dataset.sortTitle);
    const byDate = (a, b) => Number(a.dataset.publishedAt) - Number(b.dataset.publishedAt);

    switch (this.#sort) {
      case 'title-asc':
        return cards.sort(byText);
      case 'title-desc':
        return cards.sort((a, b) => byText(b, a));
      case 'date-desc':
        return cards.sort((a, b) => byDate(b, a));
      case 'date-asc':
        return cards.sort(byDate);
      default:
        return cards.sort((a, b) => Number(b.dataset.addedAt) - Number(a.dataset.addedAt));
    }
  }

  #render() {
    this.#visible = this.#sortCards([...this.#cards.values()].filter(card => this.#matches(card)));

    this.grid.replaceChildren(...this.#visible);
    this.#renderPills();
    this.#updateFilterCounts();

    this.noResultsEl.hidden = this.#visible.length > 0;
  }

  #renderPills() {
    const groups = this.#config.filters ?? [];

    const pills = [...this.#filters].flatMap(([key, values]) => [...values].map(value => {
      const node = WishlistPage.#clone(this.pillTemplate);
      const link = node.querySelector('[data-wishlist-pill]');

      link.dataset.filterKey = key;
      link.dataset.value = value;

      const label = groups.find(group => group.key === key)?.label ?? key;
      node.querySelector('[data-label]').textContent = `${label}: ${this.#valueLabel(key, value)}`;

      return node;
    }));

    this.pillsList.replaceChildren(...pills);
    this.pillsEl.hidden = !pills.length;
  }

  #updateFilterCounts() {
    this.filtersInner.querySelectorAll('[data-filter-group]').forEach(group => {
      const selected = this.#filters.get(group.dataset.filterGroup)?.size ?? 0;
      const counter = group.querySelector('.facets__selected');

      counter.textContent = `(${selected})`;
      counter.classList.toggle('hidden', selected === 0);
    });
  }

  #uncheck({ filterKey, value }) {
    const input = this.filtersInner.querySelector(`input[data-filter-key="${filterKey}"][value="${CSS.escape(value)}"]`);

    if (input) {
      input.checked = false;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    this.#dropFilterValue(filterKey, value);
    this.#render();
  }

  // An empty Set left in the map would match nothing and blank the grid.
  #dropFilterValue(key, value) {
    const values = this.#filters.get(key);
    if (!values) return;

    values.delete(value);

    if (!values.size) this.#filters.delete(key);
  }

  #clearFilters() {
    this.#filters.clear();
    this.filtersInner.querySelectorAll('input:checked').forEach(input => {
      input.checked = false;
    });

    this.#render();
  }

  #closeFilters() {
    if (this.filtersEl.hasAttribute('open')) this.filtersEl.querySelector('summary').click();
  }

  #togglePicker(card) {
    const open = !card.classList.contains('wishlist-card--picker-open');

    this.#closePickers();
    card.classList.toggle('wishlist-card--picker-open', open);
    card.querySelector('[data-wishlist-picker-open]')?.setAttribute('aria-expanded', String(open));
  }

  #closePickers() {
    this.querySelectorAll('.wishlist-card--picker-open').forEach(card => {
      card.classList.remove('wishlist-card--picker-open');
      card.querySelector('[data-wishlist-picker-open]')?.setAttribute('aria-expanded', 'false');
    });
  }

  async #addToCart(button) {
    const card = button.closest('[data-wishlist-card]');
    const body = new FormData();

    body.append('id', button.dataset.variantId);
    body.append('quantity', 1);

    card.classList.add('wishlist-card--loading');

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body
      });

      if (!response.ok) throw new Error(`Cart responded with ${response.status}`);

      const item = await response.json();
      if (item.status) throw new Error(item.description || item.message);

      document.dispatchEvent(new CustomEvent('update-cart', {
        detail: { cartLine: 1, quantity: 1, element: card, response: item }
      }));
      document.dispatchEvent(new CustomEvent('open-cart'));

      this.#closePickers();
    } catch (error) {
      console.error('Wishlist: add to cart failed', error);
    } finally {
      card.classList.remove('wishlist-card--loading');
    }
  }

  async #remove(card) {
    const item = this.#itemsByHandle.get(card.dataset.handle);
    if (!item) return;

    card.classList.add('wishlist-card--loading');

    try {
      await this.#swymRemove(item);
    } catch (error) {
      console.error('Wishlist: remove failed', error);
      card.classList.remove('wishlist-card--loading');
      return;
    }

    card.classList.remove('wishlist-card--loading');
    this.#pushRemoveEvent(item);

    // Only the last removal can be undone - that matches the single toast.
    const removed = { item, index: this.#items.indexOf(item) };

    this.#lastRemoved = removed;
    this.#setItems(this.#items.filter(entry => entry !== item));
    this.#cards.delete(item.handle);

    this.#afterChange();
    this.#showToast();
  }

  async #undo() {
    const removed = this.#lastRemoved;
    if (!removed) return;

    try {
      await this.#swymAdd(removed.item);
    } catch (error) {
      console.error('Wishlist: undo failed', error);
      return;
    }

    // The timer may have expired, or the shopper may have removed something
    // else while Swym was answering - then this undo is stale.
    if (this.#lastRemoved !== removed) return;

    const items = [...this.#items];
    items.splice(removed.index, 0, removed.item);

    this.#setItems(items);
    this.#lastRemoved = null;
    this.#hideToast();

    await this.#fetchCards(`products/${removed.item.handle}`);
    this.#afterChange();
  }

  #swymRemove(item) {
    return Promise.all(item.entries.map(entry => new Promise((resolve, reject) => {
      if (this.#swat.removeFromWishlist) {
        this.#swat.removeFromWishlist(entry.epi, entry.empi, resolve, reject);
      } else if (this.#swat.removeFromWishList) {
        this.#swat.removeFromWishList(entry, resolve, reject);
      } else {
        reject(new Error('Swym remove API is not available'));
      }
    })));
  }

  #swymAdd(item) {
    const add = this.#swat.addToWishlist ?? this.#swat.addToWishList;

    if (!add) return Promise.reject(new Error('Swym add API is not available'));

    return Promise.all(item.entries.map(entry => new Promise((resolve, reject) => {
      add.call(this.#swat, WishlistPage.#addPayload(entry), resolve, reject);
    })));
  }

  static #addPayload(entry) {
    const fields = ['empi', 'epi', 'du', 'dt', 'iu', 'pr', 'stk', 'qty', 'vi', 'bt', 'ct', 'note', 'cprops', 'variants'];

    return Object.fromEntries(
      fields.filter(field => entry[field] != null).map(field => [field, entry[field]])
    );
  }

  #afterChange() {
    document.dispatchEvent(new CustomEvent('update-wishlist'));

    if (this.#items.length) {
      this.#render();
    } else {
      this.#showEmpty();
    }
  }

  #pushRemoveEvent(item) {
    const entry = item.entries[0];
    if (!entry?.dt) return;

    const [category, code] = entry.dt.split(' | ');
    const dataLayer = window.dataLayer || [];

    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event: 'remove_from_wishlist',
      ecommerce: {
        currency: window.ShopifyAnalytics?.meta?.currency ?? window.Shopify?.currency?.active,
        value: `${entry.pr}`,
        items: [
          {
            item_id: `${code}`,
            item_name: `${code}`,
            item_category: `${category}`,
            item_list_id: 'wishlist',
            item_list_name: 'Wishlist',
            item_variant: `${entry.sku}`,
            price: `${entry.pr}`,
            quantity: 1
          }
        ]
      }
    });
  }

  #showToast() {
    clearTimeout(this.#undoTimer);
    this.toast.classList.add('is-visible');

    this.#undoTimer = setTimeout(() => {
      this.#lastRemoved = null;
      this.#hideToast();
    }, this.undoDuration);
  }

  #hideToast() {
    clearTimeout(this.#undoTimer);
    this.toast.classList.remove('is-visible');
  }

  #updateCount() {
    const count = this.#items.length;
    const { count_one: one, count_other: other } = this.#config.strings ?? {};
    const template = count === 1 ? one : other;

    this.countEl.textContent = (template ?? '').replace('[[count]]', count);
    this.countEl.hidden = count === 0;
  }

  // loading | ready | empty | error
  #setState(state) {
    this.loader.hidden = state !== 'loading';
    this.toolbar.hidden = state !== 'ready';
    this.emptyEl.hidden = state !== 'empty';
    this.errorEl.hidden = state !== 'error';

    if (state !== 'ready') this.noResultsEl.hidden = true;
  }

  #showEmpty() {
    this.grid.innerHTML = '';
    this.#setState('empty');
  }

  #showError() {
    this.#setState('error');
  }

  #print() {
    const { print_title: title, count_one: one, count_other: other } = this.#config.strings ?? {};
    const countTemplate = this.#visible.length === 1 ? one : other;

    const heading = document.createElement('h1');
    heading.className = 'wishlist-print__title-main';
    heading.textContent = title ?? '';

    const count = document.createElement('p');
    count.className = 'wishlist-print__count';
    count.textContent = (countTemplate ?? '').replace('[[count]]', this.#visible.length);

    const list = document.createElement('ul');
    list.className = 'wishlist-print__list';
    list.append(...this.#visible.map(card => this.#printItem(card)));

    this.printArea.replaceChildren(heading, count, list);

    window.print();
  }

  #printItem(card) {
    const image = card.querySelector('img');
    const lines = [...card.querySelectorAll('.card-product__title')].map(node => node.textContent.trim());
    const price = card.querySelector('.price--on-sale')
      ? card.querySelector('.price__sale .price-item--sale')
      : card.querySelector('.price__regular .price-item--regular');

    const item = document.createElement('li');
    item.className = 'wishlist-print__item';

    const picture = document.createElement('img');
    picture.src = image?.currentSrc || image?.src || '';
    picture.alt = '';

    const title = document.createElement('p');
    title.className = 'wishlist-print__title';
    title.textContent = lines.join(' - ');

    const amount = document.createElement('p');
    amount.className = 'wishlist-print__price';
    amount.textContent = price?.textContent.replace(/\s+/g, ' ').trim() ?? '';

    item.append(picture, title, amount);

    return item;
  }
}

customElements.define('wishlist-page', WishlistPage);
